import type {
  AreaRole,
  AtaPresenca,
  DecisionStatus,
  DecisionTipo,
  MeetingSchedule,
  MeetingTipo,
  OrgChartRow,
  SettingKey,
  SettingRow,
  Settings,
  TaskPriority,
  TaskStatus,
} from '@supply/contracts'

import { describeDbError, getSupabase } from '@/lib/supabase'
import { isViewingAs, VIEW_AS_UNAVAILABLE } from '@/lib/view-as'
import { appEnv } from '@/lib/env'
import { getBridgeSupabaseAccessToken } from '@/lib/supabase-auth-token'

import { mockEngine } from './mock/mode'

import type {
  Area,
  AreaMember,
  AreaPerformanceRow,
  Ata,
  AtaTipo,
  AtaLocal,
  AtaParticipant,
  Decision,
  LoadScopeRow,
  Member,
  MemberRole,
  NavCounterRow,
  Notification,
  PerformanceTarget,
  Routine,
  RoutineCheck,
  SsoUser,
  Task,
  TaskAction,
  TaskComment,
  TaskEvent,
} from './types'

/**
 * Acesso ao schema `supply`. Toda função ou devolve o dado ou lança `Error`
 * com a mensagem pronta para a tela (describeDbError) — a autorização é do
 * banco (RLS + triggers); aqui não há regra de permissão.
 */

type Result<T> = PromiseLike<{ data: T | null; error: unknown }>

async function q<T>(query: Result<T>, fallback: string): Promise<T> {
  const { data, error } = await query
  if (error) throw new Error(describeDbError(error, fallback))
  return data as T
}

/** Escritas devolvem `{ok, reason}` para o useWrite mostrar o motivo. */
export type WriteResult<T = void> = { ok: true; value: T } | { ok: false; reason: string }

async function w<T>(query: Result<T>, fallback: string): Promise<WriteResult<T>> {
  const { data, error } = await query
  if (error) return { ok: false, reason: describeDbError(error, fallback) }
  return { ok: true, value: data as T }
}

/**
 * Ponto único de acesso ao banco. Com "dados fictícios" armado (SIS-465, só
 * dev admin, só nesta aba), responde o banco em memória de `mock/` e nada sai
 * para o Supabase — leitura ou escrita.
 */
const sb = () => (mockEngine()?.client as unknown as ReturnType<typeof getSupabase> | undefined) ?? getSupabase()

// ---------------------------------------------------------------------------
// Referência: áreas, pessoas, papéis, metas
// ---------------------------------------------------------------------------

export interface Reference {
  areas: Area[]
  members: Member[]
  areaMembers: AreaMember[]
  roles: MemberRole[]
  targets: PerformanceTarget[]
  /** `supply.setting`: só as chaves que o gestor mudou (o resto é padrão). */
  settings: SettingRow[]
  /** Falha ao ler as configurações: a sessão segue com os padrões. */
  settingsError: string | null
  /**
   * Dev admins fantasmas (`supply.ghost_member_ids`, ADR 0008): só os ids. A
   * sessão tira essas pessoas das listas e mostra "Administração do sistema"
   * como autor para quem não é admin.
   */
  ghostIds: string[]
}

export async function loadReference(): Promise<Reference> {
  const [areas, members, areaMembers, roles, targets, settings, ghostIds] = await Promise.all([
    q<Area[]>(sb().from('area').select('*').order('sort_order'), 'Falha ao carregar áreas'),
    q<Member[]>(sb().from('member').select('*').order('full_name'), 'Falha ao carregar pessoas'),
    q<AreaMember[]>(sb().from('area_member').select('area_id, member_id, role'), 'Falha ao carregar vínculos'),
    q<MemberRole[]>(sb().from('member_role').select('member_id, role'), 'Falha ao carregar papéis'),
    q<PerformanceTarget[]>(sb().from('performance_target').select('id, area_id, month, target_pct'), 'Falha ao carregar metas'),
    // Configuração nunca derruba o app: sem a tabela (migration não aplicada)
    // ou com erro de rede, vale o padrão do código.
    q<SettingRow[]>(sb().from('setting').select('key, value, updated_at, updated_by'), 'Falha ao carregar configurações').then(
      (rows) => ({ rows, error: null }),
      (e: unknown) => ({ rows: [] as SettingRow[], error: e instanceof Error ? e.message : 'Falha ao carregar configurações' }),
    ),
    // Nunca trava o login: sem a RPC (banco sem a migration), ninguém é fantasma.
    q<string[] | null>(sb().rpc('ghost_member_ids'), 'Falha ao carregar administradores').then(
      (ids) => ids ?? [],
      () => [] as string[],
    ),
  ])
  return { areas, members, areaMembers, roles, targets, settings: settings.rows, settingsError: settings.error, ghostIds }
}

/** Concede ou revoga dev admin: só admin, e nunca o último (`supply.guard_admin_role`). */
export const setAdmin = (memberId: string, on: boolean) =>
  on
    ? w(sb().from('member_role').insert({ member_id: memberId, role: 'admin', reason: 'tela de administradores' }), 'Não foi possível conceder dev admin')
    : w(sb().from('member_role').delete().eq('member_id', memberId).eq('role', 'admin'), 'Não foi possível revogar dev admin')

/** Organograma: pessoa × vínculo, já recortado pelo papel de quem vê (`supply.org_chart`). */
export const loadOrgChart = () => q<OrgChartRow[]>(sb().rpc('org_chart'), 'Falha ao carregar o organograma')

/** Grava uma configuração (só gestor; o banco valida tipo e tamanho). */
export const saveSetting = <K extends SettingKey>(key: K, value: Settings[K]) =>
  w(sb().from('setting').upsert({ key, value }, { onConflict: 'key' }), 'Não foi possível salvar a configuração')

/** Volta ao padrão: sem linha, vale o padrão do código. */
export const resetSettings = (keys: readonly SettingKey[]) =>
  w(sb().from('setting').delete().in('key', keys), 'Não foi possível restaurar o padrão')

/** Situação da sessão no Supply (`supply.my_access_status`): quem está pendente não lê nem a própria ficha. */
export interface AccessStatus {
  readonly status: 'ok' | 'pendente' | 'sem_ficha'
  readonly member_id: string | null
  readonly full_name: string | null
  readonly email: string | null
}

export const myAccessStatus = async (): Promise<AccessStatus | null> => {
  const rows = await q<AccessStatus[]>(sb().rpc('my_access_status'), 'Falha ao consultar o acesso')
  return rows[0] ?? null
}

export const listSsoUsers = (search: string) =>
  q<SsoUser[]>(sb().rpc('list_sso_users', { p_search: search || null }), 'Falha ao listar usuários do SSO')

export const createMemberFromSso = (p: {
  userId: string
  fullName: string
  areas: { area_id: string; role: AreaRole }[]
  gestor: boolean
}) =>
  w(
    sb().rpc('create_member_from_sso', {
      p_user_id: p.userId,
      p_full_name: p.fullName || null,
      p_areas: p.areas,
      p_gestor: p.gestor,
    }),
    'Não foi possível cadastrar a pessoa',
  )

/** Traz a pessoa do SSO para o time da área como executor (gestor ou gerente da área). */
export const addTeamExecutor = (areaId: string, userId: string, fullName?: string) =>
  w(
    sb().rpc('add_team_executor', { p_area: areaId, p_user_id: userId, p_full_name: fullName || null }),
    'Não foi possível trazer a pessoa para o time',
  )

/** Tira o executor do time; sem outro vínculo, a ficha é desativada (no banco). */
export const removeTeamExecutor = (areaId: string, memberId: string) =>
  w(sb().rpc('remove_team_executor', { p_area: areaId, p_member: memberId }), 'Não foi possível tirar a pessoa do time')

export const updateMember = (id: string, patch: Partial<Pick<Member, 'full_name' | 'is_active'>>) =>
  w(sb().from('member').update(patch).eq('id', id), 'Não foi possível salvar a pessoa')

export const setAreaMember = (areaId: string, memberId: string, role: AreaRole | null) =>
  role
    ? w(
        sb().from('area_member').upsert({ area_id: areaId, member_id: memberId, role }, { onConflict: 'area_id,member_id' }),
        'Não foi possível salvar o vínculo (cada área tem um só responsável)',
      )
    : w(sb().from('area_member').delete().eq('area_id', areaId).eq('member_id', memberId), 'Não foi possível remover o vínculo')

export const setGestor = (memberId: string, on: boolean) =>
  on
    ? w(sb().from('member_role').insert({ member_id: memberId, role: 'gestor', reason: 'tela de pessoas' }), 'Não foi possível conceder gestor')
    : w(sb().from('member_role').delete().eq('member_id', memberId).eq('role', 'gestor'), 'Não foi possível remover gestor')

export const updateArea = (id: string, patch: Partial<Pick<Area, 'name' | 'sort_order' | 'color_token' | 'is_active' | 'tipo' | 'pc_area'>>) =>
  w(sb().from('area').update(patch).eq('id', id), 'Não foi possível salvar a área')

export const upsertTarget = (areaId: string | null, month: string | null, pct: number) =>
  w(
    sb().from('performance_target').upsert(
      { area_id: areaId, month: month ? `${month}-01` : null, target_pct: pct },
      { onConflict: 'area_id,month' },
    ),
    'Não foi possível salvar a meta',
  )

export const deleteTarget = (id: string) =>
  w(sb().from('performance_target').delete().eq('id', id), 'Não foi possível remover a meta')

// ---------------------------------------------------------------------------
// Tarefas e ações
// ---------------------------------------------------------------------------

export const tasksForWeek = (week: string) =>
  q<Task[]>(sb().rpc('tasks_for_week', { p_week: week }), 'Falha ao carregar as tarefas da semana')

/** Tarefas com prazo (ou lançamento) num intervalo de datas — calendário e desempenho. */
export async function tasksInRange(from: string, to: string, weekFrom: string, weekTo: string): Promise<Task[]> {
  return q<Task[]>(
    sb()
      .from('task')
      .select('*')
      .is('deleted_at', null)
      .or(`and(prazo.gte.${from},prazo.lte.${to}),and(prazo.is.null,semana_lancamento.gte.${weekFrom},semana_lancamento.lte.${weekTo})`)
      .order('prazo', { nullsFirst: false })
      .limit(1000),
    'Falha ao carregar tarefas',
  )
}

export const openTasks = () =>
  q<Task[]>(
    sb().from('task').select('*').is('deleted_at', null).in('status', ['pendente', 'em_processo']).order('prazo').limit(1000),
    'Falha ao carregar tarefas em aberto',
  )

export const tasksSinceWeek = (week: string) =>
  q<Task[]>(
    sb()
      .from('task')
      .select('*')
      .is('deleted_at', null)
      .or(`semana_lancamento.gte.${week},status.in.(pendente,em_processo)`)
      .limit(1000),
    'Falha ao carregar tarefas',
  )

export async function tasksByIds(ids: string[]): Promise<Task[]> {
  const unique = [...new Set(ids)]
  const out: Task[] = []
  for (let i = 0; i < unique.length; i += 150) {
    out.push(...(await q<Task[]>(sb().from('task').select('*').in('id', unique.slice(i, i + 150)), 'Falha ao carregar tarefas')))
  }
  return out
}

export const taskByKey = (key: string) =>
  q<Task | null>(sb().from('task').select('*').eq('key', key).maybeSingle(), 'Falha ao carregar a tarefa')

export const trashedTasks = () =>
  q<Task[]>(
    sb().from('task').select('*').not('deleted_at', 'is', null).order('deleted_at', { ascending: false }).limit(500),
    'Falha ao carregar a lixeira',
  )

export const actionsForTasks = async (taskIds: string[]): Promise<TaskAction[]> => {
  if (!taskIds.length) return []
  const out: TaskAction[] = []
  // PostgREST aceita `in` grande, mas URL tem limite: em lotes.
  for (let i = 0; i < taskIds.length; i += 150) {
    const chunk = taskIds.slice(i, i + 150)
    out.push(
      ...(await q<TaskAction[]>(
        sb().from('task_action').select('*').in('task_id', chunk).order('position'),
        'Falha ao carregar ações',
      )),
    )
  }
  return out
}

/** Ações soltas (sem tarefa) visíveis na semana: da semana, ou abertas de antes. */
export const looseActionsForWeek = (week: string) =>
  q<TaskAction[]>(
    sb()
      .from('task_action')
      .select('*')
      .is('task_id', null)
      .lte('semana', week)
      .or(`semana.eq.${week},done.eq.false`)
      .order('due_date', { nullsFirst: false }),
    'Falha ao carregar ações soltas',
  )

export const actionsDueBetween = (from: string, to: string) =>
  q<TaskAction[]>(
    sb().from('task_action').select('*').gte('due_date', from).lte('due_date', to).order('due_date'),
    'Falha ao carregar ações',
  )

export interface NewTaskInput {
  area_id: string
  title: string
  description?: string | null
  priority?: TaskPriority
  qtd_prevista?: number
  prazo?: string | null
  executor_id?: string | null
  executor_text?: string | null
  status_note?: string | null
  semana_lancamento?: string
  actions?: { texto: string; due_date?: string | null; due_time?: string | null }[]
}

export const createTask = (input: NewTaskInput) =>
  w<Task>(sb().rpc('create_task', { p: input }), 'Não foi possível lançar a tarefa')

export type TaskPatch = Partial<
  Pick<
    Task,
    | 'area_id'
    | 'responsavel_id'
    | 'executor_id'
    | 'executor_text'
    | 'title'
    | 'description'
    | 'priority'
    | 'status'
    | 'position'
    | 'qtd_prevista'
    | 'qtd_entregue'
    | 'prazo'
    | 'status_note'
    | 'cancel_reason'
    | 'q3_bom'
    | 'q3_pena'
  >
>

/**
 * Update com `select()`: sem permissão o RLS filtra a linha e o PostgREST
 * devolve zero linhas sem erro — aqui isso vira recusa explícita.
 */
export async function updateTask(id: string, patch: TaskPatch): Promise<WriteResult<Task>> {
  const r = await w<Task[]>(sb().from('task').update(patch).eq('id', id).select(), 'Não foi possível salvar a tarefa')
  if (!r.ok) return r
  if (!r.value.length) return { ok: false, reason: 'Sem permissão para alterar esta tarefa.' }
  return { ok: true, value: r.value[0] as Task }
}

export const moveTask = (id: string, status: TaskStatus, position?: number, qtdEntregue?: number) =>
  w<Task>(
    sb().rpc('move_task', {
      p_task: id,
      p_status: status,
      p_position: position ?? null,
      p_qtd_entregue: qtdEntregue ?? null,
    }),
    'Não foi possível mover a tarefa',
  )

export const trashTask = (id: string) => w(sb().rpc('trash_task', { p_task: id }), 'Não foi possível excluir')
export const restoreTask = (id: string) => w(sb().rpc('restore_task', { p_task: id }), 'Não foi possível restaurar')

export const createAction = (a: {
  task_id: string | null
  area_id: string
  semana: string
  texto: string
  due_date?: string | null
  due_time?: string | null
  position?: number
}) => w<TaskAction[]>(sb().from('task_action').insert(a).select(), 'Não foi possível adicionar a ação')

export async function updateAction(
  id: string,
  patch: Partial<Pick<TaskAction, 'texto' | 'due_date' | 'due_time' | 'done' | 'position' | 'area_id' | 'task_id'>>,
): Promise<WriteResult> {
  const r = await w<TaskAction[]>(sb().from('task_action').update(patch).eq('id', id).select('id'), 'Não foi possível salvar a ação')
  if (!r.ok) return r
  if (!r.value.length) return { ok: false, reason: 'Sem permissão para alterar esta ação.' }
  return { ok: true, value: undefined }
}

export const deleteAction = (id: string) =>
  w(sb().from('task_action').delete().eq('id', id), 'Não foi possível excluir a ação')

export const taskEvents = (taskId: string) =>
  q<TaskEvent[]>(
    sb().from('task_event').select('*').eq('task_id', taskId).order('created_at', { ascending: false }).limit(100),
    'Falha ao carregar o histórico',
  )

// ---------------------------------------------------------------------------
// Desempenho
// ---------------------------------------------------------------------------

export const performanceMonth = (month: string) =>
  q<AreaPerformanceRow[]>(sb().rpc('performance_month', { p_month: month }), 'Falha ao calcular o desempenho')

/** Desempenho por área num intervalo de dias (mesma régua; agregado no banco). */
export const performanceRange = (from: string, to: string) =>
  q<AreaPerformanceRow[]>(sb().rpc('performance_range', { p_from: from, p_to: to }), 'Falha ao calcular o desempenho')

/**
 * Tarefas do Dashboard num período (`supply.tasks_in_period`): em jogo em
 * alguma semana do período ou com prazo nele. Vem em páginas de 1000 (limite
 * do PostgREST) — um ano inteiro cabe em poucas idas.
 */
export async function tasksInPeriod(from: string, to: string): Promise<Task[]> {
  const PAGE = 1000
  const out: Task[] = []
  for (let start = 0; ; start += PAGE) {
    const page = await q<Task[]>(
      sb().rpc('tasks_in_period', { p_from: from, p_to: to }).range(start, start + PAGE - 1),
      'Falha ao carregar as tarefas do período',
    )
    out.push(...page)
    if (page.length < PAGE) return out
  }
}

// ---------------------------------------------------------------------------
// Q3, decisões e ATA
// ---------------------------------------------------------------------------

export const decisionsForTasks = async (taskIds: string[]): Promise<Decision[]> => {
  if (!taskIds.length) return []
  const out: Decision[] = []
  for (let i = 0; i < taskIds.length; i += 150) {
    out.push(
      ...(await q<Decision[]>(
        sb().from('q3_decision').select('*').in('task_id', taskIds.slice(i, i + 150)).order('position'),
        'Falha ao carregar decisões',
      )),
    )
  }
  return out
}

export const listDecisions = () =>
  q<Decision[]>(sb().from('q3_decision').select('*').order('created_at', { ascending: false }).limit(1000), 'Falha ao carregar decisões')

/** Linha da decisão com a ATA de origem (link) e, se revogada, a que a substituiu. */
const DECISION_SELECT = '*, ata:ata_id(semana, area_id, tipo), substituta:substituida_por(id, decisao)'

/** Filtros da tela de Decisões — aplicados no banco, com paginação por `range`. */
export interface DecisionFilter {
  status: DecisionStatus | null
  tipo: DecisionTipo | null
  areaId: string | null
  ownerId: string | null
  search: string
  /** vencidas = prazo antes de hoje · semana = hoje até +7 dias · sem = sem prazo. */
  due: 'vencidas' | 'semana' | 'sem' | null
  today: string
}

function plusDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

function decisionQuery(f: Omit<DecisionFilter, 'status'>, select: string, head = false) {
  let query = sb().from('q3_decision').select(select, { count: 'exact', head })
  if (f.areaId) query = query.eq('area_id', f.areaId)
  if (f.ownerId) query = query.eq('owner_id', f.ownerId)
  if (f.tipo) query = query.eq('tipo', f.tipo)
  // Full-text no banco (`busca`, portuguese + unaccent, GIN); a RLS vale como em qualquer select.
  const term = f.search.trim()
  if (term) query = query.textSearch('busca', term, { config: 'pt_unaccent', type: 'websearch' })
  if (f.due === 'vencidas') query = query.lt('prazo', f.today)
  if (f.due === 'semana') query = query.gte('prazo', f.today).lte('prazo', plusDays(f.today, 7))
  if (f.due === 'sem') query = query.is('prazo', null)
  return query
}

async function countOf(query: PromiseLike<{ count: number | null; error: unknown }>): Promise<number> {
  const { count, error } = await query
  if (error) throw new Error(describeDbError(error, 'Falha ao contar decisões'))
  return count ?? 0
}

/** Uma página de decisões (prazo mais próximo primeiro) e o total do filtro. */
export async function decisionsPage(f: DecisionFilter, page: number, pageSize: number): Promise<{ rows: Decision[]; total: number }> {
  let query = decisionQuery(f, DECISION_SELECT)
  if (f.status) query = query.eq('status', f.status)
  const from = (page - 1) * pageSize
  const { data, count, error } = await query
    .order('prazo', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false })
    .range(from, from + pageSize - 1)
  // Página além do fim (lista encolheu desde o link): PostgREST responde 416 (PGRST103).
  if (error && (error as { code?: string }).code === 'PGRST103') {
    let head = decisionQuery(f, 'id', true)
    if (f.status) head = head.eq('status', f.status)
    return { rows: [], total: await countOf(head) }
  }
  if (error) throw new Error(describeDbError(error, 'Falha ao carregar decisões'))
  return { rows: (data ?? []) as unknown as Decision[], total: count ?? 0 }
}

/**
 * Contagem por status (com os demais filtros), abertas vencidas e — de quem
 * vê a tela — A executar sem prazo em que é responsável ou registrou.
 */
export async function decisionCounts(
  f: Omit<DecisionFilter, 'status'>,
  memberId: string,
): Promise<{ byStatus: Record<DecisionStatus, number>; late: number; noDeadline: number }> {
  const statuses = ['aberta', 'convertida', 'concluida', 'descartada'] as const satisfies readonly DecisionStatus[]
  const [counts, late, noDeadline] = await Promise.all([
    Promise.all(statuses.map((s) => countOf(decisionQuery(f, 'id', true).eq('status', s)))),
    countOf(decisionQuery({ ...f, due: null }, 'id', true).eq('tipo', 'executar').eq('status', 'aberta').lt('prazo', f.today)),
    countOf(
      sb()
        .from('q3_decision')
        .select('id', { count: 'exact', head: true })
        .eq('tipo', 'executar')
        .eq('status', 'aberta')
        .is('prazo', null)
        .or(`owner_id.eq.${memberId},created_by.eq.${memberId}`),
    ),
  ])
  return { byStatus: Object.fromEntries(statuses.map((s, i) => [s, counts[i] ?? 0])) as Record<DecisionStatus, number>, late, noDeadline }
}

export const createDecision = (d: {
  task_id: string | null
  ata_id?: string | null
  decisao: string
  /** Registro: o banco limpa responsável e prazo. */
  tipo?: DecisionTipo
  owner_id?: string | null
  owner_text?: string | null
  prazo?: string | null
}) => w(sb().from('q3_decision').insert(d), 'Não foi possível registrar a decisão')

export const updateDecision = (
  id: string,
  patch: Partial<Pick<Decision, 'decisao' | 'tipo' | 'definida_em' | 'owner_id' | 'owner_text' | 'prazo' | 'status'>>,
) => w(sb().from('q3_decision').update(patch).eq('id', id), 'Não foi possível salvar a decisão')

/** Revoga um registro vigente por outro (o antigo fica com link para o novo). */
export const substituteDecision = (id: string, decisao: string) =>
  w<string>(sb().rpc('substitute_decision', { p_old: id, p_decisao: decisao }), 'Não foi possível substituir a decisão')

export const deleteDecision = (id: string) =>
  w(sb().from('q3_decision').delete().eq('id', id), 'Não foi possível excluir a decisão')

export const convertDecision = (id: string, target: 'task' | 'action') =>
  w<{ task_id?: string; key?: string; action_id?: string }>(
    sb().rpc('convert_decision', { p_decision: id, p_target: target }),
    'Não foi possível converter a decisão',
  )

export async function ataFor(week: string, areaId: string | null, tipo: AtaTipo = 'q3'): Promise<Ata | null> {
  let query = sb().from('ata').select('*').eq('tipo', tipo).eq('semana', week)
  query = areaId ? query.eq('area_id', areaId) : query.is('area_id', null)
  return q<Ata | null>(query.maybeSingle(), 'Falha ao carregar a ATA')
}

export const listAtas = () =>
  q<Ata[]>(sb().from('ata').select('*').order('semana', { ascending: false }).limit(500), 'Falha ao carregar as ATAs')

export const ataParticipants = (ataId: string) =>
  q<AtaParticipant[]>(
    sb().from('ata_participant').select('*').eq('ata_id', ataId).order('created_at'),
    'Falha ao carregar participantes',
  )

export const createAta = (
  week: string,
  areaId: string | null,
  patch: Partial<Pick<Ata, 'resp_reuniao_id' | 'data' | 'conteudo'>> = {},
  tipo: AtaTipo = 'q3',
) =>
  w<Ata[]>(sb().from('ata').insert({ tipo, semana: week, area_id: areaId, ...patch }).select(), 'Não foi possível criar a ATA')

export const updateAta = (
  id: string,
  patch: Partial<Pick<Ata, 'data' | 'inicio' | 'fim' | 'local' | 'resp_reuniao_id' | 'resp_ata_id' | 'observacoes' | 'conteudo'>>,
) => w(sb().from('ata').update(patch).eq('id', id), 'Não foi possível salvar a ATA')

export const ataLocais = () =>
  q<AtaLocal[]>(sb().from('ata_local').select('*').order('name'), 'Falha ao carregar os locais')

export const createAtaLocal = (name: string) =>
  w<AtaLocal>(sb().from('ata_local').insert({ name: name.trim() }).select().single(), 'Não foi possível cadastrar o local')

export const renameAtaLocal = (id: string, name: string) =>
  w(sb().from('ata_local').update({ name: name.trim() }).eq('id', id), 'Não foi possível renomear o local (já existe outro com esse nome?)')

export const deleteAtaLocal = (id: string) =>
  w(sb().from('ata_local').delete().eq('id', id), 'Não foi possível remover o local')

export const addParticipant = (ataId: string, p: { member_id?: string; guest_name?: string }) =>
  w(sb().from('ata_participant').insert({ ata_id: ataId, ...p }), 'Não foi possível adicionar o participante')

export const removeParticipant = (id: string) =>
  w(sb().from('ata_participant').delete().eq('id', id), 'Não foi possível remover o participante')

export const setParticipantPresenca = (id: string, presenca: AtaPresenca) =>
  w(sb().from('ata_participant').update({ presenca }).eq('id', id), 'Não foi possível salvar a presença')

/** Confirma de uma vez a presença de quem ainda está a confirmar. */
export const markPendingPresent = (ataId: string) =>
  w(
    sb().from('ata_participant').update({ presenca: 'presente' }).eq('ata_id', ataId).eq('presenca', 'pendente'),
    'Não foi possível confirmar as presenças',
  )

export const closeAta = (id: string, snapshot: unknown) =>
  w<Ata>(sb().rpc('close_ata', { p_ata: id, p_snapshot: snapshot }), 'Não foi possível fechar a ATA')
export const markAtaSent = (id: string) =>
  w<Ata>(sb().rpc('mark_ata_sent', { p_ata: id }), 'Não foi possível marcar como enviada')
export const reopenAta = (id: string) =>
  w<Ata>(sb().rpc('reopen_ata', { p_ata: id }), 'Não foi possível reabrir a ATA')

/** Dia ideal da RAS e da Q3 (Configurações → Reuniões). Linha ausente = sem dia. */
export const meetingSchedules = () =>
  q<MeetingSchedule[]>(
    sb().from('meeting_schedule').select('tipo, dia_semana, horario, vigente_desde'),
    'Falha ao carregar o dia das reuniões',
  )

export const saveMeetingSchedule = (tipo: MeetingTipo, diaSemana: number | null, horario: string | null) =>
  w(
    sb().from('meeting_schedule').upsert({ tipo, dia_semana: diaSemana, horario }, { onConflict: 'tipo' }),
    'Não foi possível salvar o dia da reunião',
  )

/** Tipo, semana e status das ATAs desde uma semana — base das pendências de reunião. */
export const ataStatusSince = (week: string) =>
  q<Pick<Ata, 'tipo' | 'semana' | 'status'>[]>(
    sb().from('ata').select('tipo, semana, status').gte('semana', week).limit(1000),
    'Falha ao carregar as ATAs',
  )

/** Reuniões (RAS e Q3) com data no intervalo, com os convocados — agenda do Calendário. */
export async function meetingsBetween(from: string, to: string): Promise<{ atas: Ata[]; participants: AtaParticipant[] }> {
  const atas = await q<Ata[]>(
    sb().from('ata').select('*').gte('data', from).lte('data', to).order('data').limit(500),
    'Falha ao carregar as reuniões',
  )
  if (!atas.length) return { atas, participants: [] }
  const participants = await q<AtaParticipant[]>(
    sb()
      .from('ata_participant')
      .select('*')
      .in(
        'ata_id',
        atas.map((a) => a.id),
      ),
    'Falha ao carregar os convocados',
  )
  return { atas, participants }
}

// ---------------------------------------------------------------------------
// Comentários e notificações
// ---------------------------------------------------------------------------

export const taskComments = (taskId: string) =>
  q<TaskComment[]>(
    sb().from('task_comment').select('*').eq('task_id', taskId).order('created_at'),
    'Falha ao carregar comentários',
  )

export const addComment = (taskId: string, authorId: string, body: string) =>
  w(sb().from('task_comment').insert({ task_id: taskId, author_id: authorId, body }), 'Não foi possível comentar')

export const editComment = (id: string, body: string) =>
  w(sb().from('task_comment').update({ body }).eq('id', id), 'Não foi possível editar o comentário')

export const removeComment = (id: string) =>
  w(sb().from('task_comment').update({ deleted_at: new Date().toISOString() }).eq('id', id), 'Não foi possível apagar o comentário')

export const listNotifications = (limit = 50) =>
  q<Notification[]>(
    sb().from('notification').select('*').order('created_at', { ascending: false }).limit(limit),
    'Falha ao carregar avisos',
  )

export const markNotificationsRead = (ids?: string[]) =>
  w<number>(sb().rpc('mark_notifications_read', { p_ids: ids ?? null }), 'Não foi possível marcar como lido')

export const sweepDueNotifications = () =>
  w<number>(sb().rpc('sweep_due_notifications'), 'Falha ao gerar avisos de prazo')

/** Avisa RAS/Q3 atrasada (uma vez por reunião; idempotente no banco). */
export const sweepMeetingNotifications = () =>
  w<number>(sb().rpc('sweep_meeting_notifications'), 'Falha ao gerar avisos de reunião')

/** Follow-up de decisão vencida ao superior imediato (uma vez por decisão e prazo). */
export const sweepDecisionNotifications = () =>
  w<number>(sb().rpc('sweep_decision_notifications'), 'Falha ao gerar avisos de decisão')

// ---------------------------------------------------------------------------
// Contadores da barra lateral (SIS-508)
// ---------------------------------------------------------------------------

/** Pendências e novidades por rota do menu, numa chamada (RLS de quem pergunta). */
export const navCounters = () => q<NavCounterRow[]>(sb().rpc('nav_counters'), 'Falha ao carregar os contadores do menu')

/** A pessoa abriu (ou saiu de) a tela: as novidades dela zeram. */
export const markNavSeen = (route: string) =>
  w(sb().rpc('mark_nav_seen', { p_route: route }), 'Não foi possível marcar a tela como vista')

// ---------------------------------------------------------------------------
// Rotinas e Carga do time
// ---------------------------------------------------------------------------

/** Rotinas que a pessoa lê (RLS: executor só as dele e as do time da área). */
export const listRoutines = () =>
  q<Routine[]>(sb().from('routine').select('*').order('title'), 'Falha ao carregar as rotinas')

export const routineChecksBetween = (from: string, to: string) =>
  q<RoutineCheck[]>(
    sb().from('routine_check').select('*').gte('occurs_on', from).lte('occurs_on', to),
    'Falha ao carregar as marcações das rotinas',
  )

export type RoutineInput = Pick<
  Routine,
  'title' | 'description' | 'assignee_id' | 'freq' | 'weekdays' | 'month_day' | 'starts_on' | 'ends_on' | 'is_active'
>

export const createRoutine = (areaId: string, input: RoutineInput) =>
  w(sb().from('routine').insert({ area_id: areaId, ...input }).select().single(), 'Não foi possível criar a rotina')

export const updateRoutine = (id: string, patch: Partial<RoutineInput>) =>
  w(sb().from('routine').update(patch).eq('id', id).select().single(), 'Não foi possível salvar a rotina')

export const deleteRoutine = (id: string) =>
  w(sb().from('routine').delete().eq('id', id), 'Não foi possível excluir a rotina')

/** Marca a ocorrência (feita/não feita); `null` desfaz a marcação. */
export const setRoutineCheck = (routineId: string, day: string, done: boolean | null) =>
  done === null
    ? w(sb().from('routine_check').delete().eq('routine_id', routineId).eq('occurs_on', day), 'Não foi possível desmarcar')
    : w(
        sb().from('routine_check').upsert({ routine_id: routineId, occurs_on: day, done }, { onConflict: 'routine_id,occurs_on' }),
        'Não foi possível marcar a ocorrência',
      )

/** Ações não feitas (Carga do time). */
export const openActions = () =>
  q<TaskAction[]>(sb().from('task_action').select('*').eq('done', false).limit(3000), 'Falha ao carregar ações em aberto')

/** Pessoas da Carga do time de quem pergunta (gestor todas; gerente o time; os outros, si). */
export const loadScope = () => q<LoadScopeRow[]>(sb().rpc('load_scope'), 'Falha ao carregar o time')

// ---------------------------------------------------------------------------
// Chamado para o departamento de Sistemas (Portal de Chamados)
// ---------------------------------------------------------------------------
// O chamado é do Portal de Chamados (pc.flexibase.com.br): departamento
// Sistemas, template "Chamado para Sistemas", sistema SUPPLY. O PC repassa ao
// Hub SISTEMAS sozinho. O Supply fala com o PC pela própria origem: o nginx da
// imagem (e o Vite, em dev) repassa `/api/chamado/*` para
// `/api/integracoes/apps/supply/*` do PC. Sem segredo no navegador: quem prova
// quem é a pessoa é o próprio token do Supabase do dev01 (o mesmo do Supply),
// que o PC confere. Contrato: notas da sessão, `contrato-supply-pc-chamado.md`.

const SUPPORT_API = '/api/chamado'
const SUPPORT_UNREACHABLE = 'Não foi possível falar com o Portal de Chamados agora. Confira a internet e tente de novo.'
const SUPPORT_SESSION = 'Sua sessão expirou. Recarregue a página para entrar de novo.'
const SUPPORT_LOCAL =
  'No ambiente local o chamado não sai daqui: o Portal de Chamados só aceita o login real do SSO. Abra pelo Portal ou rode o Supply contra o dev01.'

export interface SupportFormField {
  readonly id: string
  readonly label: string
  readonly type: string
  readonly required?: boolean
  readonly options?: readonly string[]
}

export interface SupportFormInfo {
  readonly departamento: string
  readonly template: { readonly id: string; readonly nome: string; readonly descricao?: string | null; readonly fields: readonly SupportFormField[] }
  readonly sistema: string
  readonly solicitante?: { readonly nome?: string | null; readonly email?: string | null; readonly ramal?: string | null } | null
}

export interface SupportTicketInput {
  readonly nome: string
  readonly ramal: string
  readonly assunto: string
  /** Respostas do template por id do campo (`hub_motivo`, `hub_descricao`). */
  readonly respostas: Readonly<Record<string, string>>
}

export interface SupportTicketCreated {
  readonly protocolo: string
  readonly id: string
  readonly url?: string | null
}

/** Token do Supabase do dev01: o do bridge (produção) ou o da sessão local (dev). */
async function supportToken(): Promise<string | null> {
  const bridge = getBridgeSupabaseAccessToken()
  if (bridge) return bridge
  const { data } = await sb().auth.getSession()
  return data.session?.access_token ?? null
}

async function supportCall<T>(path: string, init?: RequestInit): Promise<WriteResult<T>> {
  // Dados fictícios: simula o sucesso, nada vai ao Portal de Chamados.
  const mock = mockEngine()
  if (mock) return mock.support(path, init) as WriteResult<T>
  // Login de teste (Supabase local): o token não é do dev01 e o PC recusaria.
  // Melhor dizer isso do que culpar a internet.
  if (appEnv.authMode === 'local') return { ok: false, reason: SUPPORT_LOCAL }
  // "Ver como": o token aqui é o do gestor — nunca usá-lo fingindo ser outro.
  if (isViewingAs()) return { ok: false, reason: VIEW_AS_UNAVAILABLE }
  try {
    const token = await supportToken()
    if (!token) return { ok: false, reason: SUPPORT_SESSION }
    const headers = new Headers(init?.headers)
    headers.set('authorization', `Bearer ${token}`)
    const response = await fetch(`${SUPPORT_API}${path}`, { ...init, headers, cache: 'no-store' })
    const body = (await response.json().catch(() => null)) as ({ error?: string; erro?: string } & T) | null
    if (!response.ok) {
      const said = body?.error ?? body?.erro
      if (response.status === 401) return { ok: false, reason: said ?? SUPPORT_SESSION }
      // 400 (template), 403 (sem cadastro no PC), 404, 429 (limite) e 503 (PC
      // sem a opção do SUPPLY) trazem a frase do PC; o resto vira a genérica.
      const reason = [400, 403, 404, 413, 415, 429, 503].includes(response.status) && said ? said : SUPPORT_UNREACHABLE
      return { ok: false, reason }
    }
    if (!body) return { ok: false, reason: SUPPORT_UNREACHABLE }
    return { ok: true, value: body }
  } catch {
    return { ok: false, reason: SUPPORT_UNREACHABLE }
  }
}

/** Template vivo do PC (motivos, rótulos) e o cadastro de quem abre. */
export const supportFormOptions = () => supportCall<SupportFormInfo>('/formulario')

export const openSupportTicket = (input: SupportTicketInput) =>
  supportCall<SupportTicketCreated>('/chamados', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })

/** Envia os anexos um a um; devolve quantos falharam (o chamado já existe). */
export async function uploadSupportAttachments(ticketId: string, files: readonly File[]): Promise<number> {
  let failed = 0
  for (const file of files) {
    const qs = file.name ? `?${new URLSearchParams({ nome: file.name }).toString()}` : ''
    const r = await supportCall<{ success: true }>(`/chamados/${encodeURIComponent(ticketId)}/anexos${qs}`, {
      method: 'POST',
      headers: { 'content-type': file.type || 'application/octet-stream' },
      body: file,
    })
    if (!r.ok) failed += 1
  }
  return failed
}

// ---------------------------------------------------------------------------
// "Ver como" (SIS-438): sessão de visualização, auditada no banco
// ---------------------------------------------------------------------------

export interface ViewAsStarted {
  id: string
  target_member_id: string
  target_user_id: string
  started_at: string
  expires_at: string
}

export interface ImpersonationLogRow {
  id: string
  gestor_member_id: string
  target_member_id: string
  started_at: string
  expires_at: string
  ended_at: string | null
  end_reason: 'saiu' | 'expirou' | 'substituida' | null
  ip: string | null
  user_agent: string | null
}

/** Só gestor real abre (o banco confere); 30 min. */
export const startViewAs = (targetMemberId: string) =>
  w<ViewAsStarted>(sb().rpc('start_view_as', { p_target_member: targetMemberId }), 'Não foi possível entrar no modo visualização')

export const endViewAs = (sessionId: string, reason: 'saiu' | 'expirou' = 'saiu') =>
  w<boolean>(sb().rpc('end_view_as', { p_session: sessionId, p_reason: reason }), 'Não foi possível encerrar o modo visualização')

/** Registro das sessões (RLS: só gestor real lê). */
export const listImpersonationLog = (limit = 50) =>
  q<ImpersonationLogRow[]>(
    sb().from('impersonation_log').select('id, gestor_member_id, target_member_id, started_at, expires_at, ended_at, end_reason, ip, user_agent')
      .order('started_at', { ascending: false }).limit(limit),
    'Falha ao carregar o registro do ver como',
  )
